import { fetcher } from "../api/fetcher";
import {
  extractFileNameAndExtension,
  formatFileSize,
} from "./chatFiles";
import { FrontMessageResponse } from "../types/messages";
import { computed, nextTick, reactive, ref } from "vue";
import { MessageItem } from "../types/localMessage";
import { XDate } from "@connect/date-helper";
import {
  saveScrollPosition,
  restoreScrollPosition,
} from "./scrollHistoryChatState";
import { getDateOrToday } from "../utils/chat/date";
import { parseTextContent } from "../utils/chat/splitMessage";
import { roomState } from "./room";

export const historyId = ref("")

const historyMessages = reactive<(MessageItem & {id: string})[]>([]);
export const errorChatHistoryState = reactive({
  startMessages: false,
  denied: false,
  nextMessages: false,
});
export const loadingChatHistoryState = reactive({
  startMessages: false,
});

function addMessage(message: FrontMessageResponse) {
  if (message.type === "Text") {
    historyMessages.push({
      id: message.id,
      type: message.type,
      status: message.status,
      author: message.author,
      content: {
        text: parseTextContent(message.content.text),
      },
      createAt: XDate.parse(message.createAt).format("HH:mm"),
      createAtDate: new Date(message.createAt),
      updateAt: message.updateAt,
      deleteAt: message.deleteAt,
    });
  } else if (message.type === "File") {
    const { name, extension } = extractFileNameAndExtension(
      message.content.filename,
    );
    historyMessages.push({
      id: message.id,
      type: message.type,
      status: message.status,
      author: message.author,
      content: {
        text: parseTextContent(message.content.text),
        file: {
          filename: name,
          ext: extension,
          fileId: message.content.fileId,
          size: formatFileSize(message.content.size),
          preview: message.content.preview,
        },
      },
      createAt: XDate.parse(message.createAt).format("HH:mm"),
      createAtDate: new Date(message.createAt),
      updateAt: message.updateAt,
      deleteAt: message.deleteAt,
    });
  }
}

const sortMessages = () => {
  historyMessages.sort((a, b) => a.createAtDate.getTime() - b.createAtDate.getTime());
};

export interface DateMark {
  type: "DateMark";
  text: string;
}

export const historyMessagesList = computed(() => {
  const res: ((MessageItem & {id: string}) | DateMark)[] = [];
  let last_date_mark: string = "";
  for (const message of historyMessages) {
    const dateMark = getDateOrToday(message.createAtDate);
    if (last_date_mark !== dateMark) {
      last_date_mark = dateMark;
      res.push({
        type: "DateMark",
        text: dateMark,
      });
    }
    res.push(message);
  }
  return res;
});

export async function getHistoryMessages() {
  loadingChatHistoryState.startMessages = true;
  try {
    const res = (await fetcher.get({
      path: `/chat/start-history-messages/${historyId.value}`,
    })) as {
      data: FrontMessageResponse[];
    };
    if (res.data.length < 20) {
      isFullHisstory.value = true;
    }
    for (const message of res.data) {
      addMessage(message);
    }
    sortMessages();
    errorChatHistoryState.startMessages = false;
  } catch(err: any) {
    errorChatHistoryState.startMessages = true;
    if(err.status === 401) {
      errorChatHistoryState.denied = true
    }
  } finally {
    loadingChatHistoryState.startMessages = false;
  }
}

const isFullHisstory = ref(false);

export async function getNextHistory() {
  if (isFullHisstory.value) return;
  if (historyMessages.length) {
    const res = (await fetcher.get({
      path: `/chat/pag-history-prev/${historyId.value}/${historyMessages[0].id}`,
    })) as FrontMessageResponse[];
    saveScrollPosition();
    for (const message of res) {
      addMessage(message);
    }
    sortMessages();
    nextTick(() => {
      restoreScrollPosition();
    });
  } else {
    isFullHisstory.value = true;
  }
}

export function resetHistory() {
  historyMessages.length = 0
  loadingChatHistoryState.startMessages = false
  errorChatHistoryState.startMessages = false
  errorChatHistoryState.nextMessages = false
  isFullHisstory.value = false
}

export async function clearChatHistory() {
  await fetcher.delete({
    path: `/chat/${roomState.id}`,
  });
}
